/**
 * useDeferredLoading Hook
 * 로딩 상태가 일정 시간 이상 지속될 때만 로딩 UI를 표시
 * 짧은 로딩 시간에 화면이 깜빡이는 현상 방지
 */
import { useState, useEffect, useRef } from 'react';

/**
 * 지연 로딩 상태 훅
 * @param isLoading - 실제 로딩 상태
 * @param delay - 로딩 UI 표시 전 대기 시간 (기본값: 200ms)
 * @returns 지연된 로딩 상태 (delay 이후에만 true)
 */
export const useDeferredLoading = (isLoading: boolean, delay = 200): boolean => {
    const [showLoading, setShowLoading] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (isLoading) {
            // 로딩 시작 시 타이머 설정
            timerRef.current = setTimeout(() => {
                setShowLoading(true);
            }, delay);
        } else {
            // 로딩 완료 시 타이머 취소 및 로딩 상태 해제
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            setShowLoading(false);
        }

        // 클린업
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isLoading, delay]);

    return showLoading;
};

export default useDeferredLoading;
